import { MarketingLayout } from '@/components/layout/MarketingLayout';
import { Card } from '@/components/ui/card';
import { SEO } from '@/components/SEO';

export default function TermsPage() {
  return (
    <MarketingLayout>
      <SEO
        title="Terms of Service - Prozpector"
        description="Read Prozpector's terms of service and user agreement."
        url="https://prozpector.com/terms"
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white to-gray-50 py-20 md:py-28">
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6">
              Terms of Service
            </h1>
            <p className="text-xl text-gray-600 mb-4">
              Last updated: January 1, 2024
            </p>
            <p className="text-lg text-gray-600">
              Please read these terms carefully before using Prozpector.
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <Card className="p-8 md:p-12">
              <div className="space-y-8">
                {/* Agreement */}
                <div>
                  <h2 className="text-2xl font-bold mb-4">1. Agreement to Terms</h2>
                  <p className="text-gray-600 mb-4">
                    By accessing or using Prozpector ("Service," "Platform," "we," "us," or "our"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, you do not have permission to access the Service.
                  </p>
                  <p className="text-gray-600">
                    We reserve the right to modify these Terms at any time. We will notify you of material changes via email or through the Platform. Your continued use after changes constitutes acceptance of the modified Terms.
                  </p>
                </div>

                {/* Account Registration */}
                <div>
                  <h2 className="text-2xl font-bold mb-4">2. Account Registration</h2>
                  <p className="text-gray-600 mb-4">To use Prozpector, you must:</p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Be at least 18 years old and capable of forming a binding contract</li>
                    <li>Provide accurate, current, and complete registration information</li>
                    <li>Maintain the security of your account credentials</li>
                    <li>Immediately notify us of any unauthorized access or security breach</li>
                    <li>Accept responsibility for all activities under your account</li>
                  </ul>
                  <p className="text-gray-600 mt-4">
                    You may not share your account with others or create multiple accounts. We reserve the right to terminate accounts that violate these Terms.
                  </p>
                </div>

                {/* Acceptable Use */}
                <div>
                  <h2 className="text-2xl font-bold mb-4">3. Acceptable Use Policy</h2>
                  <h3 className="text-xl font-semibold mb-3">3.1 Permitted Uses</h3>
                  <p className="text-gray-600 mb-4">You may use Prozpector for:</p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 mb-4">
                    <li>Legitimate B2B sales prospecting and outreach</li>
                    <li>Managing customer relationships and communications</li>
                    <li>Automating repetitive prospecting tasks</li>
                    <li>Analyzing sales performance and metrics</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-3">3.2 Prohibited Uses</h3>
                  <p className="text-gray-600 mb-4">You may NOT use Prozpector to:</p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Send spam, unsolicited bulk emails, or illegal content</li>
                    <li>Violate CAN-SPAM Act, GDPR, CCPA, or other applicable laws</li>
                    <li>Harvest or scrape data without authorization</li>
                    <li>Impersonate others or misrepresent your identity</li>
                    <li>Distribute malware, viruses, or malicious code</li>
                    <li>Interfere with or disrupt the Platform's operation</li>
                    <li>Attempt to gain unauthorized access to our systems</li>
                    <li>Resell or redistribute the Service without permission</li>
                    <li>Use the Platform for illegal or fraudulent activities</li>
                    <li>Send emails to purchased or rented lists without proper consent</li>
                  </ul>
                  <p className="text-gray-600 mt-4">
                    <strong>Violation of this Acceptable Use Policy may result in immediate account suspension or termination without refund.</strong>
                  </p>
                </div>

                {/* BYOK Model */}
                <div>
                  <h2 className="text-2xl font-bold mb-4">4. Bring Your Own Keys (BYOK) Model</h2>
                  <p className="text-gray-600 mb-4">
                    Prozpector operates on a BYOK model. You are responsible for:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Obtaining and maintaining your own API keys from third-party providers (SendGrid, Apollo, etc.)</li>
                    <li>Paying for all third-party API usage directly to those providers</li>
                    <li>Complying with all terms and conditions of your third-party providers</li>
                    <li>Ensuring your API credentials are valid and have sufficient credits/quota</li>
                    <li>Monitoring your third-party API usage and costs</li>
                  </ul>
                  <p className="text-gray-600 mt-4">
                    Prozpector is not responsible for third-party API costs, service interruptions, or policy violations with your providers. Your API keys are encrypted and stored securely, but you acknowledge the inherent risks of providing credentials to any third-party platform.
                  </p>
                </div>

                {/* Payment Terms */}
                <div>
                  <h2 className="text-2xl font-bold mb-4">5. Payment and Billing</h2>
                  <h3 className="text-xl font-semibold mb-3">5.1 Subscription Fees</h3>
                  <p className="text-gray-600 mb-4">
                    You agree to pay all fees associated with your chosen subscription plan. Fees are:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 mb-4">
                    <li>Billed in advance on a monthly or annual basis</li>
                    <li>Non-refundable except as required by law or our 14-day money-back guarantee</li>
                    <li>Automatically renewed unless you cancel before the renewal date</li>
                    <li>Subject to change with 30 days' notice (existing subscriptions grandfathered for current billing cycle)</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-3">5.2 Payment Method</h3>
                  <p className="text-gray-600 mb-4">
                    Payments are processed securely through Stripe. By providing payment information, you authorize us to charge your payment method for all fees. You must update your payment information if it changes or expires.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">5.3 Failed Payments</h3>
                  <p className="text-gray-600">
                    If payment fails, we will attempt to charge your payment method up to 3 times. After repeated failures, your account may be suspended or downgraded until payment is received.
                  </p>
                </div>

                {/* Cancellation */}
                <div>
                  <h2 className="text-2xl font-bold mb-4">6. Cancellation and Termination</h2>
                  <h3 className="text-xl font-semibold mb-3">6.1 Your Right to Cancel</h3>
                  <p className="text-gray-600 mb-4">
                    You may cancel your subscription at any time from your account settings. Cancellations take effect at the end of your current billing period. You will retain access until that date. No refunds are provided for partial billing periods except within our 14-day money-back guarantee window.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">6.2 Our Right to Terminate</h3>
                  <p className="text-gray-600 mb-4">
                    We may suspend or terminate your account immediately if:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>You violate these Terms or our Acceptable Use Policy</li>
                    <li>Your account is used for illegal or fraudulent activities</li>
                    <li>We receive complaints about spam or abuse from your account</li>
                    <li>Payment is past due for more than 15 days</li>
                    <li>We're required to do so by law or court order</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-3 mt-4">6.3 Effect of Termination</h3>
                  <p className="text-gray-600">
                    Upon termination, your access to the Platform will cease, and your data will be deleted within 30 days. You remain responsible for all fees incurred before termination. We are not liable for any loss resulting from account termination.
                  </p>
                </div>

                {/* Data Ownership */}
                <div>
                  <h2 className="text-2xl font-bold mb-4">7. Data Ownership and Usage</h2>
                  <h3 className="text-xl font-semibold mb-3">7.1 Your Data</h3>
                  <p className="text-gray-600 mb-4">
                    You retain all rights to data you upload, create, or generate using Prozpector ("Your Data"). You grant us a limited license to process Your Data solely to provide the Service.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">7.2 Our Rights</h3>
                  <p className="text-gray-600 mb-4">
                    We may use anonymized, aggregated data for analytics, research, and improving the Platform. This anonymized data cannot identify you or your prospects.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">7.3 Data Export</h3>
                  <p className="text-gray-600">
                    You may export Your Data at any time through the Platform's export features. We provide data in CSV or JSON format.
                  </p>
                </div>

                {/* Intellectual Property */}
                <div>
                  <h2 className="text-2xl font-bold mb-4">8. Intellectual Property Rights</h2>
                  <p className="text-gray-600 mb-4">
                    The Platform, including its code, design, features, and content (excluding Your Data), is owned by Prozpector and protected by copyright, trademark, and other intellectual property laws.
                  </p>
                  <p className="text-gray-600">
                    You may not copy, modify, distribute, sell, or reverse engineer any part of the Platform without our explicit written permission. The Prozpector name and logo are our trademarks and may not be used without authorization.
                  </p>
                </div>

                {/* Warranties */}
                <div>
                  <h2 className="text-2xl font-bold mb-4">9. Warranties and Disclaimers</h2>
                  <h3 className="text-xl font-semibold mb-3">9.1 Service "As Is"</h3>
                  <p className="text-gray-600 mb-4">
                    THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">9.2 No Guarantee of Results</h3>
                  <p className="text-gray-600 mb-4">
                    We do not guarantee that the Platform will:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Meet your specific requirements or produce specific results</li>
                    <li>Operate without interruption, errors, or security vulnerabilities</li>
                    <li>Be compatible with all third-party services or APIs</li>
                    <li>Result in successful sales outcomes or prospect responses</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-3 mt-4">9.3 Third-Party Services</h3>
                  <p className="text-gray-600">
                    We are not responsible for the availability, performance, or policies of third-party services you integrate with Prozpector (SendGrid, Apollo, etc.). Your use of those services is governed by their respective terms.
                  </p>
                </div>

                {/* Limitation of Liability */}
                <div>
                  <h2 className="text-2xl font-bold mb-4">10. Limitation of Liability</h2>
                  <p className="text-gray-600 mb-4">
                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, PROZPECTOR SHALL NOT BE LIABLE FOR:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Any indirect, incidental, special, consequential, or punitive damages</li>
                    <li>Loss of profits, revenue, data, or business opportunities</li>
                    <li>Damages resulting from third-party API failures or costs</li>
                    <li>Unauthorized access to or alteration of your data</li>
                    <li>Conduct of other users or third parties</li>
                  </ul>
                  <p className="text-gray-600 mt-4">
                    <strong>Our total liability to you for all claims arising from your use of the Platform shall not exceed the amount you paid us in the 12 months before the claim.</strong>
                  </p>
                </div>

                {/* Indemnification */}
                <div>
                  <h2 className="text-2xl font-bold mb-4">11. Indemnification</h2>
                  <p className="text-gray-600">
                    You agree to indemnify and hold harmless Prozpector, its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 mt-4">
                    <li>Your violation of these Terms or applicable laws</li>
                    <li>Your use of the Platform or third-party APIs</li>
                    <li>Content you submit or send through the Platform</li>
                    <li>Infringement of third-party rights</li>
                    <li>Your negligence or misconduct</li>
                  </ul>
                </div>

                {/* Compliance */}
                <div>
                  <h2 className="text-2xl font-bold mb-4">12. Legal Compliance</h2>
                  <p className="text-gray-600 mb-4">
                    You are solely responsible for ensuring your use of Prozpector complies with all applicable laws, including but not limited to:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li><strong>CAN-SPAM Act:</strong> All emails must include accurate sender information and unsubscribe links</li>
                    <li><strong>GDPR:</strong> Obtain proper consent for processing EU/EEA residents' data</li>
                    <li><strong>CCPA:</strong> Respect California residents' privacy rights</li>
                    <li><strong>TCPA:</strong> Comply with telephone consumer protection regulations</li>
                    <li><strong>Anti-Spam Laws:</strong> Follow your jurisdiction's email marketing regulations</li>
                  </ul>
                  <p className="text-gray-600 mt-4">
                    Violations may result in account termination and legal action.
                  </p>
                </div>

                {/* Dispute Resolution */}
                <div>
                  <h2 className="text-2xl font-bold mb-4">13. Dispute Resolution</h2>
                  <h3 className="text-xl font-semibold mb-3">13.1 Informal Resolution</h3>
                  <p className="text-gray-600 mb-4">
                    Before filing a claim, you agree to contact us at legal@prozpector.com to resolve the dispute informally. We'll attempt to resolve within 30 days.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">13.2 Arbitration</h3>
                  <p className="text-gray-600 mb-4">
                    If informal resolution fails, disputes will be resolved through binding arbitration under the American Arbitration Association rules. Arbitration will take place in San Francisco, California. You waive the right to participate in class action lawsuits.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">13.3 Exceptions</h3>
                  <p className="text-gray-600">
                    Either party may seek injunctive relief in court for intellectual property violations or other urgent matters. Small claims court disputes are also exempt from arbitration.
                  </p>
                </div>

                {/* Governing Law */}
                <div>
                  <h2 className="text-2xl font-bold mb-4">14. Governing Law</h2>
                  <p className="text-gray-600">
                    These Terms are governed by the laws of the State of California and the United States, without regard to conflict of law principles. Any legal action must be brought in the state or federal courts located in San Francisco County, California.
                  </p>
                </div>

                {/* Miscellaneous */}
                <div>
                  <h2 className="text-2xl font-bold mb-4">15. Miscellaneous</h2>
                  <p className="text-gray-600 mb-4"><strong>Entire Agreement:</strong> These Terms constitute the entire agreement between you and Prozpector.</p>
                  <p className="text-gray-600 mb-4"><strong>Severability:</strong> If any provision is found unenforceable, the remaining provisions remain in effect.</p>
                  <p className="text-gray-600 mb-4"><strong>Waiver:</strong> Our failure to enforce any right doesn't waive that right.</p>
                  <p className="text-gray-600 mb-4"><strong>Assignment:</strong> You may not assign these Terms without our consent. We may assign to affiliates or acquirers.</p>
                  <p className="text-gray-600"><strong>Force Majeure:</strong> We're not liable for delays caused by circumstances beyond our reasonable control.</p>
                </div>

                {/* Contact */}
                <div>
                  <h2 className="text-2xl font-bold mb-4">16. Contact Information</h2>
                  <p className="text-gray-600 mb-4">
                    For questions about these Terms, contact us:
                  </p>
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <p className="text-gray-600"><strong>Email:</strong> legal@prozpector.com</p>
                    <p className="text-gray-600"><strong>Address:</strong> Prozpector, Inc., San Francisco, CA, United States</p>
                  </div>
                </div>

                {/* Acknowledgment */}
                <div className="border-t pt-8 mt-8 bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-600 font-semibold mb-2">
                    BY USING PROZPECTOR, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS OF SERVICE.
                  </p>
                  <p className="text-sm text-gray-500">
                    Last updated: January 1, 2024
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
